// src/components/foam/FoamFlow.js
//
// State-machine orchestrator for the Foam-direction redesign.
// Mounted by App.js when REACT_APP_FLAVOR=preview (see config/flavor.js).
//
// Responsibilities:
//   - Auth state tracking (Firebase signInWithPopup)
//   - SKU parsing from URL (/r/:sku) or input (/)
//   - Product lookup (PRODUCTS map)
//   - Registration lookup (registrationService.checkRegistration)
//   - Screen selection based on (URL, auth, registration) signals
//
// Phase 1 scope: end-to-end Landing → Register → Success on preview collections.
// Other screens are mounted on their routes for visual review but their data
// wiring matures in Phase 2/3/4.

import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import { signInWithPopup, GoogleAuthProvider, OAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { COLLECTIONS, FLAVOR_LABEL, IS_PREVIEW } from '../../config/flavor';
import { checkRegistration, registerProductWithVerification, updateRegistrationDetails } from '../../services/registrationService';
import { shouldAutoVerifyRetailer, getVerificationMethod } from '../../services/shopifyVerificationService';

import FoamLanding from './FoamLanding';
import FoamRegister from './FoamRegister';
import FoamRegistered from './FoamRegistered';
import FoamMyQuiver from './FoamMyQuiver';
import FoamOwnerDetail from './FoamOwnerDetail';
import FoamLostAndFound from './FoamLostAndFound';
import FoamAlreadyClaimed from './FoamAlreadyClaimed';
import FoamWarranty from './FoamSupport'; // file hosts the warranty screen (split 2026-06-05)
import FoamChat from './FoamChat';

// ─────────────────────────────────────────────────────────────────────
// Product catalogue — mirrors PRODUCTS in ProductRegistration.js.
// (Phase 2: extract to /src/config/products.js)
// ─────────────────────────────────────────────────────────────────────

const PRODUCTS = {
  GLIDE: {
    name: 'Glide',
    sizes: ['55', '66', '77', '88', '99', '111'],
    image: 'https://cdn.shopify.com/s/files/1/0642/2799/9902/files/792438d8-c3cd-41e2-bc20-47d348a665e8.png?v=1739836117',
  },
  ROLLINGWAKA: {
    name: 'Rolling Waka',
    sizes: ['Medium', 'Large'],
    image: 'https://cdn.shopify.com/s/files/1/0642/2799/9902/files/RollingWakaHumanShadow.png?v=1739480879',
  },
  GLIDEDAYBAG: {
    name: 'Glide Day Bag',
    sizes: ['55', '66', '77', '88', '99', '111'],
    image: 'https://cdn.shopify.com/s/files/1/0642/2799/9902/files/WakaGlideDaybag.png?v=1740086016',
  },
};

// Parse a SKU like "GLIDE-88-V2-A0427" into { family, size, version, serial }
function parseSku(sku) {
  if (!sku) return null;
  const parts = sku.toUpperCase().split('-');
  const familyKey = parts[0];
  const productDef = PRODUCTS[familyKey];
  if (!productDef) return null;
  return {
    sku: sku.toUpperCase(),
    family: familyKey,
    productName: productDef.name,
    size: parts[1] || '',
    version: parts[2] || '',
    serial: parts[3] || '',
    image: productDef.image,
    // Board volume in liters (was m² — Nigel, 2026-06-05)
    caption: [parts[3], parts[2], parts[1] ? `${parts[1]} L` : ''].filter(Boolean).join(' · '),
    name: `${productDef.name.toUpperCase()} ${parts[1] || ''}`.trim(),
  };
}

// ─────────────────────────────────────────────────────────────────────
// Preview-flavor banner — visible reminder we're not on prod.
// ─────────────────────────────────────────────────────────────────────

function PreviewBanner() {
  if (!IS_PREVIEW) return null;
  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        background: 'var(--foam-orange)', color: 'var(--foam-carbon)',
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
        fontSize: 12, letterSpacing: '0.14em',
        textTransform: 'uppercase', fontWeight: 600,
        padding: '6px 12px',
        textAlign: 'center',
        zIndex: 9999,
      }}
    >
      ● {FLAVOR_LABEL} — data is isolated, not production
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Auth helpers — Apple + Google via popup
// ─────────────────────────────────────────────────────────────────────

const signInWithApple = async () => {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  return (await signInWithPopup(auth, provider)).user;
};

const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  return (await signInWithPopup(auth, provider)).user;
};

// ─────────────────────────────────────────────────────────────────────
// Route components
// ─────────────────────────────────────────────────────────────────────

// /  — root SKU input (no QR)
// /r/:sku  — QR landing (SKU pre-filled). Branches by registration state.
// /r/:sku?owner=1 — owner arrived from the L&F "Is this your board?" link:
//   suppress the lost-found redirect so they can sign in here.
function LandingRoute({ user, onSignedIn }) {
  const { sku: skuFromUrl } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ownerSignIn = new URLSearchParams(location.search).get('owner') === '1';
  const [product, setProduct] = useState(skuFromUrl ? parseSku(skuFromUrl) : null);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(!!skuFromUrl);

  const [authError, setAuthError] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  // If SKU is in URL, check whether it's already registered.
  // Re-runs when auth state changes (2026-06-05 fix): the private
  // owner-constrained query only works once signed in — without the re-run,
  // an owner signing in from this screen was routed to Register and hit the
  // duplicate guard dead-end.
  useEffect(() => {
    if (!skuFromUrl) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const result = await checkRegistration(skuFromUrl.toUpperCase());
        if (cancelled) return;
        setRegistration(result.exists ? result.data : null);
      } catch (err) {
        console.error('checkRegistration failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [skuFromUrl, user]);

  // Routing branches
  useEffect(() => {
    if (loading) return;
    if (!skuFromUrl) return; // root /, stay on landing
    if (registration) {
      // Already registered. Branches:
      if (!user) {
        // Unauthenticated viewer → Lost & Found public view — unless they
        // tapped "Is this your board? Sign in" (owner=1), in which case stay
        // here and show the sign-in buttons.
        if (!ownerSignIn) {
          navigate(`/r/${skuFromUrl}/lost-found`, { replace: true });
        }
      } else if (user.uid === registration.userId) {
        // Owner viewer → owner detail
        navigate(`/boards/${skuFromUrl}`, { replace: true });
      } else {
        // Authenticated non-owner → transfer prompt
        navigate(`/r/${skuFromUrl}/transfer`, { replace: true });
      }
    } else if (user) {
      // Authenticated, not registered → go to register screen
      navigate(`/r/${skuFromUrl}/register`, { replace: true });
    }
    // Else: not registered + not auth → stay on landing (show sign-in buttons)
  }, [loading, registration, user, skuFromUrl, ownerSignIn, navigate]);

  const handleApple = async () => {
    setAuthError(null);
    setSigningIn(true);
    try {
      const u = await signInWithApple();
      onSignedIn(u);
      // Root-screen sign-in ("Already registered?") → straight to the quiver.
      // SKU flows are routed by the registration-check effect instead.
      if (!skuFromUrl) navigate('/boards');
    } catch (e) {
      console.error(e);
      // Surface the Firebase error code — generic copy was hiding the real
      // cause during the 2026-06-04 smoke test (e.g. auth/unauthorized-domain
      // vs auth/popup-blocked need different fixes).
      setAuthError(`Apple sign-in failed${e.code ? ` (${e.code})` : ''}. Try Google or try again.`);
    } finally {
      setSigningIn(false);
    }
  };

  const handleGoogle = async () => {
    setAuthError(null);
    setSigningIn(true);
    try {
      const u = await signInWithGoogle();
      onSignedIn(u);
      // Root-screen sign-in ("Already registered?") → straight to the quiver.
      if (!skuFromUrl) navigate('/boards');
    } catch (e) {
      console.error(e);
      setAuthError('Google sign-in failed. Try Apple or try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleSerialSubmit = (sku) => {
    navigate(`/r/${sku}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-[12px] tracking-[0.14em] uppercase text-foam-ink-2">
        ● Loading…
      </div>
    );
  }

  return (
    <FoamLanding
      product={product}
      onSignInApple={handleApple}
      onSignInGoogle={handleGoogle}
      onSerialSubmit={handleSerialSubmit}
      authError={authError}
      signingIn={signingIn}
    />
  );
}

function RegisterRoute({ user }) {
  const { sku: skuFromUrl } = useParams();
  const navigate = useNavigate();
  const product = parseSku(skuFromUrl);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Guard (2026-06-05): if this serial is already registered, never show the
  // register form — owners go to their board, others to the transfer prompt.
  // Prevents the duplicate-guard dead-end Nigel hit on prod.
  useEffect(() => {
    if (!user || !skuFromUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await checkRegistration(skuFromUrl.toUpperCase());
        if (cancelled || !result.exists) return;
        if (result.data.userId === user.uid) {
          navigate(`/boards/${skuFromUrl}`, { replace: true });
        } else {
          navigate(`/r/${skuFromUrl}/transfer`, { replace: true });
        }
      } catch (e) {
        // Non-fatal — the write-time duplicate guard still protects us.
        console.error('Register pre-check failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user, skuFromUrl, navigate]);

  if (!user) {
    // Bounce back to landing — they need to auth first
    navigate(`/r/${skuFromUrl}`, { replace: true });
    return null;
  }
  if (!product) {
    return (
      <FoamLanding product={null} onSerialSubmit={(s) => navigate(`/r/${s}`)} />
    );
  }

  const onSubmit = async (selectedRetailer) => {
    setSubmitting(true);
    setError(null);
    try {
      const productData = {
        sku: product.sku,
        productName: product.productName,
        size: product.size,
        version: product.version,
        serial: product.serial,
        purchaseLocation: selectedRetailer,
      };
      // Phase 2 will add the new findability fields. Phase 1 uses legacy defaults.
      // NOTE (2026-06-04 smoke-test fix): registerProductWithVerification destructures
      // purchaseLocation from verificationData (matching the legacy ProductRegistration.js
      // call shape) — it must be present HERE or it overwrites the productData value with
      // undefined and Firestore rejects the write. getVerificationMethod's signature is
      // (shopifyMatch, retailer) — Phase 1 has no Shopify match, so pass false.
      const verificationData = {
        purchaseLocation: selectedRetailer,
        shopifyMatch: false,
        verificationMethod: getVerificationMethod(false, selectedRetailer),
        autoVerified: shouldAutoVerifyRetailer(selectedRetailer),
      };
      await registerProductWithVerification(productData, user, verificationData);
      navigate(`/r/${product.sku}/registered`);
    } catch (e) {
      console.error('Registration failed:', e);
      // Owner re-registering their own board → take them home instead of
      // stranding them on an error (2026-06-05).
      if ((e.message || '').includes('already registered to your account')) {
        navigate(`/boards/${product.sku}`, { replace: true });
        return;
      }
      setError(e.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FoamRegister
      product={product}
      matchedRetailer={null /* Phase 1: skip Shopify auto-match preselect */}
      onSubmit={onSubmit}
      submitting={submitting}
      error={error}
    />
  );
}

function RegisteredRoute({ user }) {
  const { sku: skuFromUrl } = useParams();
  const navigate = useNavigate();
  const product = parseSku(skuFromUrl);
  if (!user) {
    navigate(`/r/${skuFromUrl}`, { replace: true });
    return null;
  }
  return (
    <FoamRegistered
      product={product}
      registrationDate={new Date()}
      onDone={() => navigate('/boards')}
    />
  );
}

function MyQuiverRoute({ user }) {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
      return;
    }
    (async () => {
      try {
        const q = query(collection(db, COLLECTIONS.registrations), where('userId', '==', user.uid));
        const snap = await getDocs(q);
        const regs = snap.docs.map((d) => {
          const data = d.data();
          // Decorate with productImage from the catalogue
          const fam = (data.sku || '').split('-')[0]?.toUpperCase();
          const productImage = PRODUCTS[fam]?.image || null;
          return { id: d.id, ...data, productImage };
        });
        setRegistrations(regs);
      } catch (e) {
        console.error('Quiver fetch failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-[12px] tracking-[0.14em] uppercase text-foam-ink-2">
        ● Loading your boards…
      </div>
    );
  }

  return (
    <FoamMyQuiver
      user={user}
      registrations={registrations}
      onSelect={(reg) => navigate(`/boards/${reg.sku}`)}
      onAddAnother={() => navigate('/')}
      onSignOut={async () => {
        await signOut(auth);
        navigate('/', { replace: true });
      }}
    />
  );
}

function OwnerDetailRoute({ user }) {
  const { sku: skuFromUrl } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate(`/r/${skuFromUrl}`, { replace: true });
      return;
    }
    (async () => {
      try {
        const result = await checkRegistration(skuFromUrl.toUpperCase());
        if (result.exists && result.data.userId === user.uid) {
          setRegistration(result.data);
        } else if (result.exists) {
          // Not the owner — bounce to transfer prompt
          navigate(`/r/${skuFromUrl}/transfer`, { replace: true });
        } else {
          navigate(`/r/${skuFromUrl}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user, skuFromUrl, navigate]);

  if (loading) return null;

  const product = parseSku(skuFromUrl);
  return (
    <FoamOwnerDetail
      product={product}
      registration={registration}
      onSave={async (updates) => {
        // Auto-saved (debounced) from FoamOwnerDetail — no alerts; the
        // component renders Saving…/Saved/retry states. Errors must propagate.
        await updateRegistrationDetails(registration.id, {
          ...updates,
          lastUpdated: new Date(),
        });
      }}
      onPreviewAsFinder={() => navigate(`/r/${skuFromUrl}/lost-found?preview=true`)}
      onWarranty={() => navigate(`/boards/${skuFromUrl}/warranty`)}
      onSupport={() => navigate(`/boards/${skuFromUrl}/support`)}
      onDone={() => navigate('/boards')}
    />
  );
}

// Warranty (Nigel, 2026-06-05) — policy from waka.surf; Support is FoamChat
function WarrantyRoute({ user }) {
  const { sku: skuFromUrl } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate(`/r/${skuFromUrl}`, { replace: true });
      return;
    }
    (async () => {
      try {
        const result = await checkRegistration(skuFromUrl.toUpperCase());
        if (result.exists && result.data.userId === user.uid) {
          setRegistration(result.data);
        } else {
          navigate(`/r/${skuFromUrl}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user, skuFromUrl, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-[12px] tracking-[0.14em] uppercase text-foam-ink-2">
        ● Loading…
      </div>
    );
  }
  if (!registration) return null;

  return (
    <FoamWarranty
      product={parseSku(skuFromUrl)}
      registration={registration}
      onBack={() => navigate(`/boards/${skuFromUrl}`)}
    />
  );
}

// Support (Nigel, 2026-06-05) — straight into Viento, auto-opened
function ChatRoute() {
  const { sku: skuFromUrl } = useParams();
  const navigate = useNavigate();
  return <FoamChat onBack={() => navigate(`/boards/${skuFromUrl}`)} />;
}

function LostFoundRoute() {
  const { sku: skuFromUrl } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const preview = new URLSearchParams(location.search).get('preview') === 'true';
  const [publicRegistration, setPublicRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = await checkRegistration(skuFromUrl.toUpperCase());
        if (result.exists) {
          const r = result.data;
          if (typeof r.id === 'string' && r.id.startsWith('public-')) {
            // Came from publicRegistrations — ALREADY privacy-filtered by the
            // regeneratePublicRegistration trigger. Pass fields through as-is.
            // (2026-06-05 fix: re-applying the legacy client-side filter here
            // nulled email/message because public docs carry no findability
            // flags — finders saw "no way to be contacted" despite opt-in.)
            setPublicRegistration({
              sku: skuFromUrl.toUpperCase(),
              productName: r.productName,
              size: r.size,
              registeredUserName: r.registeredUserName || null,
              registeredUserEmail: r.registeredUserEmail || null,
              phoneNumber: r.phoneNumber || null,
              ownerCity: r.ownerCity || null,
              ownerMessage: r.ownerMessage || null,
              preferredContact: ['phone', 'email'].includes(r.preferredContact)
                ? r.preferredContact
                : null,
            });
          } else {
            // Private registration (owner preview path) — apply the privacy
            // filter client-side, falling back to legacy fields.
            setPublicRegistration({
              sku: skuFromUrl.toUpperCase(),
              productName: r.productName,
              size: r.size,
              registeredUserName: (r.findability?.showFirstName ?? r.showName ?? true)
                ? (r.customDisplayName || r.registeredUserName || '').split(' ')[0] || null
                : null,
              registeredUserEmail: (r.findability?.showEmail ?? false) ? r.registeredUserEmail : null,
              phoneNumber: (r.findability?.showPhone ?? false) ? r.phoneNumber : null,
              phoneIsWhatsApp: !!((r.findability?.showPhone ?? false) && r.findability?.phoneIsWhatsApp),
              ownerCity: (r.findability?.showCity ?? false) ? r.ownerCity : null,
              ownerMessage: (r.findability?.showOwnerMessage ?? !!r.contactMessage)
                ? (r.ownerMessage || r.contactMessage)
                : null,
              // 'via-waka' removed 2026-06-05 — legacy values map to null
              preferredContact: ['phone', 'email'].includes(r.findability?.preferredContact)
                ? r.findability.preferredContact
                : null,
            });
          }
        }
      } catch (e) {
        console.error('Public registration fetch failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [skuFromUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-[12px] tracking-[0.14em] uppercase text-foam-ink-2">
        ● Loading…
      </div>
    );
  }

  return (
    <FoamLostAndFound
      publicRegistration={publicRegistration}
      preview={preview}
      // Owner arrived from Owner Detail's "Preview as a finder" — give them a
      // way back (Nigel, 2026-06-05).
      onClosePreview={preview ? () => navigate(`/boards/${skuFromUrl}`) : undefined}
      // Logged-out owner scanning their own board — route to sign-in with the
      // lost-found redirect suppressed (Nigel, 2026-06-05).
      onOwnerSignIn={!preview ? () => navigate(`/r/${skuFromUrl}?owner=1`) : undefined}
    />
  );
}

function TransferRoute({ user }) {
  const { sku: skuFromUrl } = useParams();
  const navigate = useNavigate();
  const [publicRegistration, setPublicRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = await checkRegistration(skuFromUrl.toUpperCase());
        if (result.exists) {
          setPublicRegistration({ ...result.data, sku: skuFromUrl.toUpperCase() });
        } else {
          navigate(`/r/${skuFromUrl}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [skuFromUrl, navigate]);

  if (loading) return null;

  return (
    <FoamAlreadyClaimed
      publicRegistration={publicRegistration}
      onRequestTransfer={() => {
        // Phase 3: call requestOwnershipTransfer Cloud Function
        alert('Transfer requests ship in Phase 3 — coming soon.');
      }}
      onCancel={() => navigate('/')}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// Top-level
// ─────────────────────────────────────────────────────────────────────

const FoamFlow = () => {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-[12px] tracking-[0.14em] uppercase text-foam-ink-2">
        ● Loading…
      </div>
    );
  }

  return (
    <BrowserRouter>
      <PreviewBanner />
      <Routes>
        <Route path="/" element={<LandingRoute user={user} onSignedIn={setUser} />} />
        <Route path="/r/:sku" element={<LandingRoute user={user} onSignedIn={setUser} />} />
        <Route path="/r/:sku/register" element={<RegisterRoute user={user} />} />
        <Route path="/r/:sku/registered" element={<RegisteredRoute user={user} />} />
        <Route path="/r/:sku/lost-found" element={<LostFoundRoute />} />
        <Route path="/r/:sku/transfer" element={<TransferRoute user={user} />} />
        <Route path="/boards" element={<MyQuiverRoute user={user} />} />
        <Route path="/boards/:sku" element={<OwnerDetailRoute user={user} />} />
        <Route path="/boards/:sku/warranty" element={<WarrantyRoute user={user} />} />
        <Route path="/boards/:sku/support" element={<ChatRoute />} />
        {/* Admin retired 2026-05-17 → helm. Route carried over from LegacyApp
            at Phase 1 promotion so bookmarked /admin links keep redirecting. */}
        <Route path="/admin" element={<AdminRedirect />} />
        <Route path="/admin/*" element={<AdminRedirect />} />
      </Routes>
    </BrowserRouter>
  );
};

function AdminRedirect() {
  useEffect(() => {
    window.location.replace('https://helm.waka.surf/customers');
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center font-mono text-[12px] tracking-[0.14em] uppercase text-foam-ink-2 text-center px-[22px]">
      ● Admin has moved to helm.waka.surf — redirecting…
    </div>
  );
}

export default FoamFlow;
